import {
  campaignInventorySeasonRange,
  getDestinationCampaignHotels,
  resolveCampaignContract,
} from "../data/destinationCampaignHotels";
import { isoDateFromDdMmYyyy } from "./agentRecentSearchPayload";
import { matchDestinationCampaignHotels } from "./injectDestinationCampaignHotels";

const CARD_ORDER = [
  "royal-belvedere-2026",
  "royal-tulip-tbilisi-2026",
  "royal-zanzibar-beach-resort-2026",
  "tembo-kiwengwa-resort-2026",
  "zanzibella-hotel-spa-2026",
];

/** Short Hebrew destination labels for campaign-tab picker (by IATA). */
const CAMPAIGN_DESTINATION_LABEL_HE = {
  HER: "כרתים",
  TBS: "טביליסי",
  ZNZ: "זנזיבר",
};

const CAMPAIGN_DESTINATION_CITY_EN = {
  HER: "Crete",
  TBS: "Tbilisi",
  ZNZ: "Zanzibar",
};

const CAMPAIGN_DESTINATION_COUNTRY = {
  HER: { code: "GR", name: "Greece", nameHe: "יוון" },
  TBS: { code: "GE", name: "Georgia", nameHe: "גאורגיה" },
  ZNZ: { code: "TZ", name: "Tanzania", nameHe: "טנזניה" },
};

/**
 * Unique campaign destinations for the agent campaigns search picker.
 * One option per IATA (e.g. ZNZ covers Royal Zanzibar + Tembo + Zanzibella).
 */
export function getCampaignDestinationOptions() {
  const byCode = new Map();

  for (const hotel of getDestinationCampaignHotels()) {
    if (!hotel?.publicPath || !hotel?.image) continue;
    const code = String(hotel.destinationCodes?.[0] || "")
      .trim()
      .toUpperCase();
    if (!code || !/^[A-Z]{3}$/.test(code)) continue;

    const contract = resolveCampaignContract(hotel);
    const hotelName =
      contract?.hotelNameHe || contract?.hotelName || hotel.displayName || "";
    const existing = byCode.get(code);
    if (existing) {
      if (hotelName && !existing.hotelNames.includes(hotelName)) {
        existing.hotelNames.push(hotelName);
      }
      continue;
    }

    const labelHe = CAMPAIGN_DESTINATION_LABEL_HE[code] || hotel.address || code;
    const cityEn = CAMPAIGN_DESTINATION_CITY_EN[code] || code;
    const country = CAMPAIGN_DESTINATION_COUNTRY[code] || {
      code: "",
      name: "",
      nameHe: "",
    };

    byCode.set(code, {
      code,
      labelHe,
      label: `${labelHe} (${code})`,
      hotelNames: hotelName ? [hotelName] : [],
      location: {
        AIRPORTCODE: code,
        CITYCODE: code,
        CITYNAME: cityEn,
        CITYNAME_HEB: labelHe,
        COUNTRYCODE: country.code,
        COUNTRYNAME: country.name,
        COUNTRYNAME_HEB: country.nameHe,
        Name: labelHe,
        name: labelHe,
        label: `${labelHe} · ${code}`,
      },
    });
  }

  const order = ["HER", "TBS", "ZNZ"];
  return [...byCode.values()].sort((a, b) => {
    const ai = order.indexOf(a.code);
    const bi = order.indexOf(b.code);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

export function buildCampaignDestinationSelectOptions() {
  return getCampaignDestinationOptions().map((row) => {
    const hotelsLine = row.hotelNames.join(" · ");
    return {
      value: row.code,
      label: hotelsLine
        ? `${row.labelHe} — ${hotelsLine}`
        : `${row.labelHe} (${row.code})`,
      location: row.location,
    };
  });
}

export function resolveCampaignDestinationCodeFromLocation(location) {
  if (!location || typeof location !== "object") return null;
  const raw =
    location.AIRPORTCODE ||
    location.CITYCODE ||
    location.airportCode ||
    location.cityCode ||
    null;
  const code = String(raw || "")
    .trim()
    .toUpperCase();
  if (!code || !/^[A-Z]{3}$/.test(code)) return null;
  const known = getCampaignDestinationOptions().some((row) => row.code === code);
  return known ? code : null;
}

export function sortCampaignHotelsForDashboard(rows) {
  return [...(rows || [])].sort((a, b) => {
    const ai = CARD_ORDER.indexOf(a.slug);
    const bi = CARD_ORDER.indexOf(b.slug);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

export function listActiveCampaignHotels() {
  const rows = getDestinationCampaignHotels().filter(
    (row) => Boolean(row?.publicPath) && Boolean(row?.image),
  );
  return sortCampaignHotelsForDashboard(rows);
}

/** Stay overlaps campaign contract season window (inclusive of open bounds). */
export function campaignStayOverlapsSeason(campaign, arriveIso, departIso) {
  if (!arriveIso || !departIso) return true;
  const { start, end } = campaignInventorySeasonRange(campaign);
  if (!start || !end) return true;
  return arriveIso < end && departIso > start;
}

/**
 * Agent campaigns-tab search: match destination, then keep hotels whose season
 * window overlaps the requested stay.
 */
export function searchCampaignHotels({
  location = null,
  searchPhrase = null,
  arrivalDate = null,
  departureDate = null,
} = {}) {
  const arriveIso =
    isoDateFromDdMmYyyy(arrivalDate) ||
    isoDateFromDdMmYyyy(searchPhrase?.checkIn) ||
    null;
  const departIso =
    isoDateFromDdMmYyyy(departureDate) ||
    isoDateFromDdMmYyyy(searchPhrase?.checkOut) ||
    null;

  const matched = matchDestinationCampaignHotels({ location, searchPhrase }).filter(
    (row) => Boolean(row?.publicPath) && Boolean(row?.image),
  );

  const inSeason = matched.filter((row) =>
    campaignStayOverlapsSeason(row, arriveIso, departIso),
  );

  return {
    hotels: sortCampaignHotelsForDashboard(inSeason),
    arriveIso,
    departIso,
    matchedDestinationCount: matched.length,
  };
}

export function normalizeRoomsForCampaignUrl(rooms) {
  const list = Array.isArray(rooms) ? rooms : [];
  if (list.length === 0) return [{ adults: 2, children: 0, childAges: [] }];
  return list.map((room) => {
    const ages = Array.isArray(room?.KidsAges)
      ? room.KidsAges.map((age) => Number(age)).filter((age) => Number.isFinite(age))
      : Array.isArray(room?.childAges)
        ? room.childAges.map((age) => Number(age)).filter((age) => Number.isFinite(age))
        : [];
    const adults = Math.max(1, Math.floor(Number(room?.AdultsCount ?? room?.adults) || 2));
    const children = ages.length || Math.max(0, Math.floor(Number(room?.children) || 0));
    return {
      adults,
      children,
      childAges: ages.slice(0, children),
    };
  });
}
